<template>
  <div class="gl-orders-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-page-header">
        <div>
          <div class="gl-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Orders</span>
          </div>
          <h1 class="gl-page-title">Online Orders</h1>
        </div>
        <button class="gl-refresh-btn" @click="manualRefresh" :class="{ 'gl-refreshing': loading }">
          <svg class="gl-refresh-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Refresh
        </button>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <!-- Pending chip -->
        <button
          class="gl-stat-chip gl-stat-chip-warn"
          :class="{ 'gl-stat-active': filters.status === 'pending' }"
          @click="quickFilter('pending')"
        >
          <span class="gl-chip-num" :class="{ 'gl-chip-pulse': !statsLoading && (stats.today?.pending > 0) }">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats.today?.pending ?? 0 }}</span>
          </span>
          <span class="gl-chip-lbl">Pending</span>
        </button>
        <div class="gl-chip-div"></div>

        <!-- Confirmed chip -->
        <button
          class="gl-stat-chip gl-stat-chip-sky"
          :class="{ 'gl-stat-active': filters.status === 'confirmed' }"
          @click="quickFilter('confirmed')"
        >
          <span class="gl-chip-num">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats.today?.confirmed ?? 0 }}</span>
          </span>
          <span class="gl-chip-lbl">Confirmed</span>
        </button>
        <div class="gl-chip-div"></div>

        <!-- Preparing chip -->
        <button
          class="gl-stat-chip gl-stat-chip-blue"
          :class="{ 'gl-stat-active': filters.status === 'preparing' }"
          @click="quickFilter('preparing')"
        >
          <span class="gl-chip-num">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats.today?.preparing ?? 0 }}</span>
          </span>
          <span class="gl-chip-lbl">Preparing</span>
        </button>
        <div class="gl-chip-div"></div>

        <!-- Ready chip -->
        <button
          class="gl-stat-chip gl-stat-chip-purple"
          :class="{ 'gl-stat-active': filters.status === 'ready' }"
          @click="quickFilter('ready')"
        >
          <span class="gl-chip-num">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats.today?.ready ?? 0 }}</span>
          </span>
          <span class="gl-chip-lbl">Ready</span>
        </button>
        <div class="gl-chip-div"></div>

        <!-- Completed chip -->
        <button
          class="gl-stat-chip gl-stat-chip-teal"
          :class="{ 'gl-stat-active': filters.status === 'completed' }"
          @click="quickFilter('completed')"
        >
          <span class="gl-chip-num">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats.today?.completed ?? 0 }}</span>
          </span>
          <span class="gl-chip-lbl">Completed</span>
        </button>
        <div class="gl-chip-div"></div>

        <!-- Revenue (not clickable) -->
        <div class="gl-stat-chip gl-stat-chip-revenue">
          <span class="gl-chip-num gl-chip-rev-num">
            <span v-if="statsLoading" class="gl-skel-num gl-skel-num-wide"></span>
            <span v-else>RM {{ formatPrice(stats.today?.revenue ?? 0) }}</span>
          </span>
          <span class="gl-chip-lbl">Today's Revenue</span>
        </div>

        <!-- Auto-refresh indicator -->
        <div class="gl-autorefresh-pill" :class="{ 'gl-ar-active': autoRefresh }">
          <button class="gl-toggle" :class="{ 'gl-toggle-on': autoRefresh }" @click="autoRefresh = !autoRefresh">
            <span class="gl-toggle-thumb"></span>
          </button>
          <span class="gl-ar-label">Auto (30s)</span>
          <span v-if="autoRefresh" class="gl-ar-dot"></span>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="gl-filter-card">
        <div class="gl-filter-row">
          <select class="gl-filter-select" v-model="filters.status" @change="fetchOrders">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select class="gl-filter-select" v-model="filters.type" @change="fetchOrders">
            <option value="">All Types</option>
            <option value="dine_in">Dine In</option>
            <option value="takeaway">Takeaway</option>
            <option value="delivery">Delivery</option>
          </select>
          <select class="gl-filter-select" v-model="filters.outlet_id" @change="fetchOrders">
            <option value="">All Branches</option>
            <option v-for="outlet in outlets" :key="outlet.id" :value="outlet.id">{{ outlet.name }}</option>
          </select>
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.date_from" @change="fetchOrders" placeholder="From">
          </div>
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.date_to" @change="fetchOrders" placeholder="To">
          </div>
          <button v-if="hasActiveFilters" class="gl-filter-clear" @click="resetFilters">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Clear
          </button>
        </div>
        <div class="gl-filter-row-2">
          <div class="gl-search-wrap">
            <svg class="gl-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" class="gl-search-input" v-model="filters.search" placeholder="Search order #, customer name or phone…" @keyup.enter="fetchOrders" @input="debouncedSearch">
          </div>
          <label class="gl-check-label">
            <input type="checkbox" class="gl-check-input" v-model="filters.today" @change="fetchOrders">
            <span class="gl-check-box"></span>
            Today only
          </label>
        </div>
      </div>

      <!-- Table card -->
      <div class="gl-table-card">

        <!-- Skeleton -->
        <template v-if="loading && !orders.length">
          <div class="gl-skel-table">
            <div v-for="i in 8" :key="i" class="gl-skel-row" :style="`animation-delay:${i*35}ms`">
              <div class="gl-skel-line w20"></div>
              <div class="gl-skel-2line" style="flex:1.2">
                <div class="gl-skel-line w50"></div>
                <div class="gl-skel-line w30"></div>
              </div>
              <div class="gl-skel-line w20"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel-line w10"></div>
              <div class="gl-skel-line w15"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel-2line">
                <div class="gl-skel-line w20"></div>
                <div class="gl-skel-line w15"></div>
              </div>
              <div style="display:flex;gap:6px;flex-shrink:0">
                <div class="gl-skel-btn"></div>
                <div class="gl-skel-btn"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Table -->
        <template v-else>
          <div class="gl-table-wrap">
            <table class="gl-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Branch</th>
                  <th>Type</th>
                  <th class="th-center">Items</th>
                  <th class="th-right">Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="order in orders"
                  :key="order.id"
                  :class="getRowClass(order)"
                >
                  <td>
                    <span class="gl-order-num">{{ order.order_number }}</span>
                  </td>
                  <td>
                    <span class="gl-cust-name">{{ order.user?.name || 'Guest' }}</span>
                    <span class="gl-cust-phone">{{ order.user?.phone }}</span>
                  </td>
                  <td class="gl-branch-cell">{{ order.outlet?.name || '—' }}</td>
                  <td>
                    <span class="gl-type-badge" :class="getTypeClass(order.type)">{{ formatOrderType(order.type) }}</span>
                    <span v-if="order.table_number" class="gl-table-num">T{{ order.table_number }}</span>
                  </td>
                  <td class="td-center">
                    <span class="gl-items-count">{{ order.items?.length || 0 }}</span>
                  </td>
                  <td class="td-right">
                    <span class="gl-order-total">RM {{ formatPrice(order.total) }}</span>
                  </td>
                  <td>
                    <span class="gl-pay-badge" :class="getPaymentClass(order.payment_status)">{{ order.payment_status }}</span>
                    <span class="gl-pay-method">{{ formatMethod(order.payment_method) }}</span>
                  </td>
                  <td>
                    <span class="gl-status-badge" :class="getStatusClass(order.status)">{{ order.status }}</span>
                  </td>
                  <td>
                    <span class="gl-date-line">{{ formatDate(order.created_at) }}</span>
                    <span class="gl-time-line">{{ formatTime(order.created_at) }}</span>
                  </td>
                  <td>
                    <div class="gl-actions">
                      <router-link :to="`/owner/orders/${order.id}`" class="gl-act-view">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View
                      </router-link>
                      <button
                        v-if="canProgress(order)"
                        class="gl-act-progress"
                        @click="progressOrder(order)"
                        :disabled="updatingId === order.id"
                      >
                        <svg v-if="updatingId !== order.id" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                        <svg v-else class="gl-spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                        {{ getNextStatusLabel(order.status) }}
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Empty state -->
                <tr v-if="!orders.length">
                  <td colspan="10">
                    <div class="gl-empty-state">
                      <div class="gl-empty-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                      </div>
                      <p>No orders found</p>
                      <span v-if="hasActiveFilters">Try clearing some filters</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="gl-pagination">
            <span class="gl-page-count">
              {{ (pagination.current_page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} of {{ pagination.total }} orders
            </span>
            <div class="gl-page-btns">
              <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchOrders(pagination.current_page - 1)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button
                v-for="p in visiblePages" :key="p"
                class="gl-page-num"
                :class="{ 'gl-page-active': p === pagination.current_page }"
                @click="fetchOrders(p)"
              >{{ p }}</button>
              <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchOrders(pagination.current_page + 1)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ownerOrderApi, ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'OrderList',

  data() {
    return {
      loading: true,
      statsLoading: true,
      updatingId: null,
      autoRefresh: true,
      refreshInterval: null,
      searchTimeout: null,
      orders: [],
      outlets: [],
      stats: {},
      filters: {
        status: '',
        type: '',
        outlet_id: '',
        date_from: '',
        date_to: '',
        search: '',
        today: true
      },
      pagination: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      for (let i = Math.max(1, current - 2); i <= Math.min(last, current + 2); i++) pages.push(i);
      return pages;
    },
    hasActiveFilters() {
      return this.filters.status || this.filters.type || this.filters.outlet_id ||
             this.filters.date_from || this.filters.date_to || this.filters.search;
    }
  },

  watch: {
    autoRefresh(val) {
      val ? this.startAutoRefresh() : this.stopAutoRefresh();
    }
  },

  mounted() {
    Promise.all([this.fetchOutlets(), this.fetchStats()]);
    this.fetchOrders();
    if (this.autoRefresh) this.startAutoRefresh();
  },

  beforeUnmount() {
    this.stopAutoRefresh();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },

  methods: {
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshInterval = setInterval(() => {
        this.fetchOrders(this.pagination.current_page, true);
        this.fetchStats(true);
      }, 30000);
    },

    stopAutoRefresh() {
      if (this.refreshInterval) { clearInterval(this.refreshInterval); this.refreshInterval = null; }
    },

    manualRefresh() {
      this.fetchOrders(this.pagination.current_page);
      this.fetchStats();
    },

    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchOrders(), 300);
    },

    quickFilter(status) {
      this.filters.status = this.filters.status === status ? '' : status;
      this.fetchOrders();
    },

    async fetchOutlets() {
      try {
        const r = await ownerBranchApi.list();
        this.outlets = r.data.data || [];
      } catch (e) { console.error(e); }
    },

    async fetchStats(silent = false) {
      try {
        if (!silent) this.statsLoading = true;
        const r = await ownerOrderApi.getStats();
        this.stats = r.data.data || {};
      } catch (e) { console.error(e); } finally {
        this.statsLoading = false;
      }
    },

    async fetchOrders(page = 1, silent = false) {
      try {
        if (!silent) this.loading = true;
        const params = { page, per_page: this.pagination.per_page, ...this.filters };
        Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null || params[k] === false) delete params[k]; });
        const r = await ownerOrderApi.list(params);
        this.orders = r.data.data.data || r.data.data;
        const meta = r.data.data.meta || r.data.data;
        if (meta.current_page) {
          this.pagination = { current_page: meta.current_page, last_page: meta.last_page, per_page: meta.per_page, total: meta.total };
        }
      } catch (e) { console.error(e); } finally {
        if (!silent) this.loading = false;
      }
    },

    resetFilters() {
      this.filters = { status: '', type: '', outlet_id: '', date_from: '', date_to: '', search: '', today: false };
      this.fetchOrders();
    },

    formatPrice(amount) { return parseFloat(amount || 0).toFixed(2); },

    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatTime(d) {
      if (!d) return '';
      return new Date(d).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' });
    },

    formatOrderType(type) {
      const map = { dine_in: 'Dine In', takeaway: 'Takeaway', delivery: 'Delivery' };
      return map[type] || type;
    },

    formatMethod(method) {
      if (!method) return '';
      const map = { cod: 'COD', fpx: 'FPX', card: 'Card', ewallet: 'eWallet', wallet: 'Wallet' };
      return map[method] || method;
    },

    getRowClass(order) {
      if (order.status === 'pending') return 'gl-row-pending';
      if (order.status === 'cancelled') return 'gl-row-cancelled';
      return '';
    },

    getTypeClass(type) {
      const map = { delivery: 'gl-type-delivery', takeaway: 'gl-type-takeaway', dine_in: 'gl-type-dine' };
      return map[type] || 'gl-type-default';
    },

    getStatusClass(status) {
      const map = {
        pending: 'gl-status-pending', confirmed: 'gl-status-confirmed',
        preparing: 'gl-status-preparing', ready: 'gl-status-ready',
        completed: 'gl-status-completed', cancelled: 'gl-status-cancelled'
      };
      return map[status] || 'gl-status-default';
    },

    getPaymentClass(status) {
      const map = { paid: 'gl-pay-paid', pending: 'gl-pay-pending', failed: 'gl-pay-failed' };
      return map[status] || 'gl-pay-default';
    },

    canProgress(order) {
      if (!['pending', 'confirmed', 'preparing', 'ready'].includes(order.status)) return false;
      // Prepaid (online/wallet) orders can't be progressed until paid; COD is exempt.
      // Mirrors the backend payment gate in Admin/Owner/OrderController::updateStatus.
      if (order.payment_method !== 'cod' && order.payment_status !== 'paid') return false;
      return true;
    },

    getNextStatusLabel(status) {
      const map = { pending: 'Confirm', confirmed: 'Prepare', preparing: 'Ready', ready: 'Complete' };
      return map[status] || '';
    },

    getNextStatus(status) {
      const map = { pending: 'confirmed', confirmed: 'preparing', preparing: 'ready', ready: 'completed' };
      return map[status];
    },

    async progressOrder(order) {
      const next = this.getNextStatus(order.status);
      if (!next) return;
      try {
        this.updatingId = order.id;
        await ownerOrderApi.updateStatus(order.id, { status: next });
        const idx = this.orders.findIndex(o => o.id === order.id);
        if (idx !== -1) this.orders[idx].status = next;
        this.fetchStats(true);
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to update order status');
      } finally {
        this.updatingId = null;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page header ── */
.gl-orders-page { padding-bottom: 40px; }
.gl-page-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }
.gl-refresh-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-refresh-btn:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-refresh-icon { transition: transform 0.15s; }
.gl-refreshing .gl-refresh-icon { animation: gl-spin 1s linear infinite; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 10px 16px; margin-bottom: 12px;
}
.gl-stat-chip {
  display: flex; flex-direction: column; align-items: flex-start;
  padding: 5px 14px 5px 0; background: none; border: none; cursor: pointer;
  border-radius: 10px; transition: background 0.12s; text-align: left;
}
.gl-stat-chip:hover { background: #f8f9fa; }
.gl-stat-chip-revenue { cursor: default; }
.gl-stat-chip-revenue:hover { background: none; }
.gl-chip-num { font-size: 20px; font-weight: 800; color: #111827; line-height: 1.1; min-height: 24px; display: flex; align-items: center; letter-spacing: -0.5px; }
.gl-chip-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; margin-top: 1px; white-space: nowrap; }
.gl-chip-div { width: 1px; height: 32px; background: #f0f1f3; margin: 0 10px; flex-shrink: 0; }
.gl-chip-rev-num { font-size: 18px; color: #d97706; }

/* Active state per chip type */
.gl-stat-active.gl-stat-chip-warn { background: #fffbf0; }
.gl-stat-active.gl-stat-chip-warn .gl-chip-num { color: #d97706; }
.gl-stat-active.gl-stat-chip-sky { background: #f0f9ff; }
.gl-stat-active.gl-stat-chip-sky .gl-chip-num { color: #0284c7; }
.gl-stat-active.gl-stat-chip-blue { background: #eff6ff; }
.gl-stat-active.gl-stat-chip-blue .gl-chip-num { color: #2563eb; }
.gl-stat-active.gl-stat-chip-purple { background: #fdf4ff; }
.gl-stat-active.gl-stat-chip-purple .gl-chip-num { color: #9333ea; }
.gl-stat-active.gl-stat-chip-teal { background: #f0f5f4; }
.gl-stat-active.gl-stat-chip-teal .gl-chip-num { color: #900C27; }

/* Pulse for urgent pending */
.gl-chip-pulse { animation: gl-pulse-num 2s ease-in-out infinite; }
@keyframes gl-pulse-num {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.65; }
}

/* Auto-refresh pill */
.gl-autorefresh-pill {
  display: inline-flex; align-items: center; gap: 7px;
  margin-left: auto; padding: 6px 12px;
  border: 1.5px solid #f0f1f3; border-radius: 20px; background: #f8f9fa;
}
.gl-ar-label { font-size: 11.5px; color: #6b7280; font-weight: 500; }
.gl-ar-dot { width: 6px; height: 6px; border-radius: 50%; background: #900C27; animation: gl-blink 1.8s ease-in-out infinite; }
@keyframes gl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }

/* Toggle switch */
.gl-toggle {
  position: relative; width: 32px; height: 18px;
  background: #e5e7eb; border: none; border-radius: 20px;
  cursor: pointer; transition: background 0.2s; flex-shrink: 0; padding: 0;
}
.gl-toggle-on { background: #900C27; }
.gl-toggle-thumb {
  position: absolute; top: 2px; left: 2px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.gl-toggle-on .gl-toggle-thumb { transform: translateX(14px); }

/* ── Filter card ── */
.gl-filter-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 12px;
  padding: 12px 14px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 10px;
}
.gl-filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.gl-filter-row-2 { display: flex; align-items: center; gap: 12px; }
.gl-filter-select {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 12px;
  font-size: 13px; color: #374151; background: #fff; outline: none;
  cursor: pointer; transition: border-color 0.15s;
}
.gl-filter-select:focus { border-color: #900C27; }
.gl-date-wrap { position: relative; display: inline-flex; align-items: center; }
.gl-date-icon { position: absolute; left: 9px; color: #9ca3af; pointer-events: none; }
.gl-date-input {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 10px 7px 28px;
  font-size: 13px; color: #374151; background: #fff; outline: none; transition: border-color 0.15s;
}
.gl-date-input:focus { border-color: #900C27; }
.gl-filter-clear {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px;
  background: #fff5f5; color: #dc2626; font-size: 12.5px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.gl-filter-clear:hover { background: #fee2e2; }
.gl-search-wrap { position: relative; flex: 1; max-width: 380px; }
.gl-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-search-input {
  width: 100%; border: 1.5px solid #e8eaf0; border-radius: 9px;
  padding: 7px 10px 7px 30px; font-size: 13.5px; color: #111827;
  outline: none; background: #fff; transition: border-color 0.15s; box-sizing: border-box;
}
.gl-search-input:focus { border-color: #900C27; }
.gl-search-input::placeholder { color: #c0c4cc; }

/* Custom checkbox */
.gl-check-label { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font-size: 13px; color: #6b7280; font-weight: 500; user-select: none; }
.gl-check-input { display: none; }
.gl-check-box {
  width: 16px; height: 16px; border: 1.5px solid #d1d5db; border-radius: 4px;
  background: #fff; transition: all 0.15s; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
}
.gl-check-input:checked + .gl-check-box { background: #900C27; border-color: #900C27; }
.gl-check-input:checked + .gl-check-box::after {
  content: ''; width: 9px; height: 5px; border-left: 2px solid #fff; border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translateY(-1px);
}

/* ── Table card ── */
.gl-table-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; }
.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 10px 14px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-center { text-align: center; }
.gl-table thead th.th-right { text-align: right; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 10px 14px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-center { text-align: center; }
.gl-table tbody td.td-right { text-align: right; }

/* Row tints */
.gl-row-pending { background: #fffdf5; }
.gl-row-pending:hover { background: #fffbeb !important; }
.gl-row-cancelled { background: #fff9f9; }
.gl-row-cancelled:hover { background: #fef2f2 !important; }

/* Cell content */
.gl-order-num { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 11.5px; font-weight: 700; color: #900C27; letter-spacing: 0.03em; }
.gl-cust-name { display: block; font-size: 13px; font-weight: 600; color: #111827; }
.gl-cust-phone { display: block; font-size: 11.5px; color: #9ca3af; }
.gl-branch-cell { font-size: 12.5px; color: #6b7280; }
.gl-table-num { display: block; font-size: 11px; color: #9ca3af; margin-top: 1px; }
.gl-items-count { font-size: 13.5px; font-weight: 700; color: #374151; }
.gl-order-total { font-size: 13.5px; font-weight: 700; color: #111827; }
.gl-date-line { display: block; font-size: 12.5px; color: #374151; white-space: nowrap; }
.gl-time-line { display: block; font-size: 11.5px; color: #9ca3af; }
.gl-pay-method { display: block; font-size: 11px; color: #9ca3af; margin-top: 1px; }

/* Type badges */
.gl-type-badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; }
.gl-type-delivery { background: #eff6ff; color: #2563eb; }
.gl-type-takeaway { background: #f0fdf4; color: #16a34a; }
.gl-type-dine     { background: #fdf4ff; color: #9333ea; }
.gl-type-default  { background: #f3f4f6; color: #6b7280; }

/* Status badges */
.gl-status-badge { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; }
.gl-status-pending   { background: #fef3c7; color: #d97706; }
.gl-status-confirmed { background: #e0f2fe; color: #0284c7; }
.gl-status-preparing { background: #dbeafe; color: #2563eb; }
.gl-status-ready     { background: #ede9fe; color: #7c3aed; }
.gl-status-completed { background: #dff0ec; color: #900C27; }
.gl-status-cancelled { background: #fee2e2; color: #dc2626; }
.gl-status-default   { background: #f3f4f6; color: #6b7280; }

/* Payment badges */
.gl-pay-badge { display: inline-block; padding: 2px 7px; border-radius: 5px; font-size: 10.5px; font-weight: 600; }
.gl-pay-paid    { background: #dff0ec; color: #900C27; }
.gl-pay-pending { background: #fef3c7; color: #d97706; }
.gl-pay-failed  { background: #fee2e2; color: #dc2626; }
.gl-pay-default { background: #f3f4f6; color: #6b7280; }

/* Actions */
.gl-actions { display: flex; align-items: center; gap: 6px; }
.gl-act-view {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border: 1.5px solid #e8eaf0; border-radius: 7px;
  background: #fff; color: #374151; font-size: 12px; font-weight: 600;
  text-decoration: none; cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.gl-act-view:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-act-progress {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border: none; border-radius: 7px;
  background: #900C27; color: #fff; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.gl-act-progress:hover:not(:disabled) { background: #1a5248; }
.gl-act-progress:disabled { opacity: 0.6; cursor: not-allowed; }

/* Empty state */
.gl-empty-state { display: flex; flex-direction: column; align-items: center; padding: 52px 20px; gap: 8px; }
.gl-empty-icon { width: 52px; height: 52px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; color: #d1d5db; margin-bottom: 4px; }
.gl-empty-state p { font-size: 14px; color: #6b7280; font-weight: 600; margin: 0; }
.gl-empty-state span { font-size: 12.5px; color: #b0b7c3; }

/* ── Pagination ── */
.gl-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-top: 1px solid #f5f6f8; }
.gl-page-count { font-size: 12.5px; color: #9ca3af; }
.gl-page-btns { display: flex; align-items: center; gap: 4px; }
.gl-page-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: 1.5px solid #e8eaf0; border-radius: 8px;
  background: #fff; color: #374151; cursor: pointer; transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gl-page-num {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 30px; height: 30px; padding: 0 6px; border: 1.5px solid #e8eaf0; border-radius: 8px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.15s;
}
.gl-page-num:hover { border-color: #900C27; color: #900C27; }
.gl-page-active { background: #900C27 !important; border-color: #900C27 !important; color: #fff !important; font-weight: 700; }

/* ── Skeleton ── */
.gl-skel-table { padding: 4px 0; }
.gl-skel-row { display: flex; align-items: center; gap: 18px; padding: 13px 14px; border-bottom: 1px solid #f5f6f8; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-row:last-child { border-bottom: none; }
.gl-skel-2line { display: flex; flex-direction: column; gap: 5px; flex-shrink: 0; }
.gl-skel-line { height: 11px; background: #f0f1f3; border-radius: 5px; flex-shrink: 0; }
.gl-skel-badge { width: 56px; height: 20px; background: #f0f1f3; border-radius: 6px; flex-shrink: 0; }
.gl-skel-btn { width: 54px; height: 27px; background: #f0f1f3; border-radius: 7px; flex-shrink: 0; }
.gl-skel-num { display: inline-block; width: 32px; height: 18px; background: #f0f1f3; border-radius: 5px; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-num-wide { width: 72px; }
.w10 { width: 10%; } .w15 { width: 15%; } .w20 { width: 20%; } .w30 { width: 30%; } .w50 { width: 50%; }

/* ── Animations ── */
@keyframes gl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes gl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.gl-spin { animation: gl-spin 0.8s linear infinite; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page-header { padding: 14px 0 8px; gap: 8px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }

  /* Stats strip: horizontal scroll pills */
  .gl-stats-strip {
    background: transparent; border: none; padding: 0;
    overflow-x: auto; flex-wrap: nowrap; gap: 8px; margin-bottom: 10px;
    scrollbar-width: none;
  }
  .gl-stats-strip::-webkit-scrollbar { display: none; }
  .gl-stat-chip {
    flex-shrink: 0; flex-direction: row; align-items: center; gap: 7px;
    padding: 7px 13px; background: #fff; border: 1.5px solid #f0f1f3; border-radius: 50px;
  }
  .gl-chip-num { font-size: 15px; min-height: unset; }
  .gl-chip-div { display: none; }
  .gl-autorefresh-pill { margin-left: 0; flex-shrink: 0; }

  /* Filter: remove card */
  .gl-filter-card { background: transparent; border: none; padding: 0; margin-bottom: 10px; }
  .gl-filter-row { flex-wrap: wrap; }
  .gl-filter-select { flex: 1; min-width: 130px; }
  .gl-date-wrap { flex: 1; min-width: 140px; }
  .gl-date-input { width: 100%; box-sizing: border-box; }
  .gl-search-wrap { max-width: 100%; }

  /* Table: hide Branch (3), Items (5), Payment (7), Date (9) */
  .gl-table thead th:nth-child(3),
  .gl-table tbody td:nth-child(3),
  .gl-table thead th:nth-child(5),
  .gl-table tbody td:nth-child(5),
  .gl-table thead th:nth-child(7),
  .gl-table tbody td:nth-child(7),
  .gl-table thead th:nth-child(9),
  .gl-table tbody td:nth-child(9) { display: none; }

  .gl-table-card { min-width: 0; }
  .gl-table { min-width: 500px; }
  .gl-table thead th, .gl-table tbody td { padding: 9px 10px; }
  .gl-act-view, .gl-act-progress { padding: 5px 8px; font-size: 11.5px; }

  /* Pagination */
  .gl-pagination { flex-direction: column; gap: 8px; align-items: center; padding: 12px; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-page-title { color: #f3f4f6; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #6b7280; }
body.dark-only .gl-refresh-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-refresh-btn:hover { border-color: #900C27; color: #a7c4be; background: #1a3330; }

body.dark-only .gl-stats-strip { background: #1f2937; border-color: #374151; }
body.dark-only .gl-chip-num { color: #f9fafb; }
body.dark-only .gl-chip-lbl { color: #6b7280; }
body.dark-only .gl-chip-div { background: #374151; }
body.dark-only .gl-stat-chip:hover { background: #111827; }
body.dark-only .gl-autorefresh-pill { background: #111827; border-color: #374151; }
body.dark-only .gl-ar-label { color: #6b7280; }
body.dark-only .gl-toggle { background: #374151; }
body.dark-only .gl-toggle-on { background: #900C27; }

body.dark-only .gl-filter-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-filter-select { background: #111827; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-date-input { background: #111827; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-search-input { background: #111827; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-check-label { color: #9ca3af; }
body.dark-only .gl-check-box { background: #111827; border-color: #374151; }

body.dark-only .gl-table-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-table thead tr { background: #111827; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-bottom-color: #374151; }
body.dark-only .gl-table tbody tr:hover { background: #111827 !important; }
body.dark-only .gl-table tbody td { color: #d1d5db; }
body.dark-only .gl-row-pending { background: #2a2310 !important; }
body.dark-only .gl-row-cancelled { background: #2a1010 !important; }
body.dark-only .gl-cust-name { color: #f9fafb; }
body.dark-only .gl-cust-phone, body.dark-only .gl-branch-cell, body.dark-only .gl-time-line, body.dark-only .gl-pay-method { color: #6b7280; }
body.dark-only .gl-order-total { color: #f9fafb; }
body.dark-only .gl-act-view { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-act-view:hover { border-color: #900C27; color: #a7c4be; background: #1a3330; }
body.dark-only .gl-empty-icon { background: #374151; color: #6b7280; }
body.dark-only .gl-empty-state p { color: #9ca3af; }
body.dark-only .gl-pagination { border-top-color: #374151; }
body.dark-only .gl-page-count { color: #6b7280; }
body.dark-only .gl-page-btn, body.dark-only .gl-page-num { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled), body.dark-only .gl-page-num:hover { border-color: #900C27; color: #a7c4be; }
body.dark-only .gl-skel-row { border-bottom-color: #374151; }
body.dark-only .gl-skel-line, body.dark-only .gl-skel-badge, body.dark-only .gl-skel-btn, body.dark-only .gl-skel-num { background: #374151; }

@media (max-width: 767px) {
  body.dark-only .gl-stats-strip { background: transparent !important; border: none !important; }
  body.dark-only .gl-stat-chip { background: #1f2937 !important; border-color: #374151 !important; }
  body.dark-only .gl-filter-card { background: transparent !important; border: none !important; }
}
</style>
